/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * AddressAutocomplete
 *
 * Main component that handles state management, events, and input for address autocomplete.
 * Uses AddressAutocompleteUI for rendering the predictions dropdown.
 *
 * Features:
 * - Debounced input handling (300ms)
 * - Country filtering support
 * - Integration with useAddressAutocomplete hook
 * - Loading and error states
 *
 * @example
 * <AddressAutocomplete
 *   onSelectPrediction={(prediction) => console.log(prediction)}
 *   countryCode="AU"
 * />
 */

import React, {useEffect, useState, useRef, useCallback} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';
import {
  Box,
  Flex,
  Input,
  Text,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {CloseIcon} from '@amblique/king-living/app/components/icons';
import AddressAutocompleteUI from './address-autocomplete-ui';
import {useAddressAutocomplete} from '@amblique/king-living/app/hooks/use-address-autocomplete';

const AddressAutocomplete = ({
  onSelectPrediction,
  onAddressSelect, // New: callback with parsed address components
  countryCode = '',
  placeholder = 'Start typing an address...',
  minChars = 3,
  debounceMs = 500,
  value,
  onChange,
  inputProps = {},
  inputId,
  ariaLabel,
  ariaLabelledBy,
  /** When set, wraps the field in a row (e.g. icon + input + clear) using these Flex props */
  inputRowProps,
  /** Shown at the start of `inputRowProps` row */
  startElement,
  /** Renders a clear control when the value is non-empty */
  showClearButton,
  /** Passed to `AddressAutocompleteUI` theme variant (e.g. `pdp`) */
  uiVariant,
  /** Props for the clear control (theme `as="button"`) */
  clearButtonProps = {},
  ...props
}) => {
  const {formatMessage} = useIntl();
  const autocomplete = useAddressAutocomplete();
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [addressInput, setAddressInput] = useState(value ?? '');
  const isUserTypingRef = useRef(false);

  const isControlled = value !== undefined && onChange !== undefined;
  const displayValue = isControlled ? value : addressInput;

  useEffect(() => {
    if (isControlled && value !== undefined) {
      setAddressInput(value);
    }
  }, [isControlled, value]);

  // Debounced autocomplete handler - only trigger when user is typing
  useEffect(() => {
    if (!isUserTypingRef.current) {
      return;
    }

    if (displayValue.length >= minChars) {
      const timer = setTimeout(() => {
        const options = {};

        if (countryCode) {
          options.components = `country:${countryCode.toLowerCase()}`;
        }
        console.log('[AddressAutocomplete] 🔍 Fetching suggestions for:', displayValue, options);
        autocomplete.getSuggestions(displayValue, options);
      }, debounceMs);

      return () => clearTimeout(timer);
    } else {
      autocomplete.clearSuggestions();
    }
  }, [displayValue, countryCode, minChars, debounceMs]);

  // Debug: log predictions changes
  useEffect(() => {
    console.log('[AddressAutocomplete] 📋 Predictions updated:', {
      count: autocomplete.predictions?.length || 0,
      loading: autocomplete.loading,
      error: autocomplete.error,
      predictions: autocomplete.predictions,
    });
  }, [autocomplete.predictions, autocomplete.loading, autocomplete.error]);

  const handleSelectPrediction = async (prediction) => {
    isUserTypingRef.current = false;
    autocomplete.clearSuggestions();

    // If onAddressSelect callback is provided, fetch place details for structured data
    if (onAddressSelect && prediction.place_id) {
      setIsLoadingDetails(true);
      console.log('[AddressAutocomplete] 📍 Fetching place details for:', prediction.place_id);

      const parsedAddress = await autocomplete.getPlaceDetails(prediction.place_id);

      setIsLoadingDetails(false);

      if (parsedAddress) {
        // Use the parsed street address instead of full description
        const streetAddress = parsedAddress.address1 || prediction.description;

        if (isControlled) {
          onChange(streetAddress);
        } else {
          setAddressInput(streetAddress);
        }

        // Call the callback with full parsed address
        onAddressSelect(parsedAddress);
        onSelectPrediction?.(prediction);
        return;
      }
    }

    // Fallback: just use the description if no callback or place details failed
    const description = prediction.description;

    if (isControlled) {
      onChange(description);
    } else {
      setAddressInput(description);
    }
    onSelectPrediction?.(prediction);
  };

  const handleInputChange = (e) => {
    isUserTypingRef.current = true;

    const v = e.target.value;

    if (isControlled) {
      onChange(v);
    } else {
      setAddressInput(v);
    }
  };

  const handleClear = useCallback(() => {
    isUserTypingRef.current = true;
    autocomplete.clearSuggestions();

    if (isControlled) {
      onChange?.('');
    } else {
      setAddressInput('');
    }
  }, [autocomplete.clearSuggestions, isControlled, onChange]);

  const inputEl = (
    <Input
      aria-label={ariaLabelledBy ? undefined : ariaLabel}
      aria-labelledby={ariaLabelledBy}
      id={inputId}
      isDisabled={isLoadingDetails}
      placeholder={placeholder}
      value={displayValue}
      onChange={handleInputChange}
      {...inputProps}
    />
  );

  // Only disable input when fetching place details (after user selects a prediction).
  // Do NOT disable while loading suggestions (autocomplete.loading) — on mobile that
  // would blur the input and dismiss the keyboard (KINDW-937).
  return (
    <Box position="relative" {...props}>
      {inputRowProps ?
        (
          <Flex {...inputRowProps}>
            {startElement}
            {inputEl}
            {showClearButton && String(displayValue || '').trim().length > 0 ?
              (
                <Box
                  alignItems="center"
                  as="button"
                  aria-label={formatMessage({
                    id: 'address_autocomplete.clear',
                    defaultMessage: 'Clear address',
                  })}
                  display="flex"
                  flexShrink={0}
                  justifyContent="center"
                  type="button"
                  onClick={handleClear}
                  {...clearButtonProps}
                  h="24px"
                  minH="24px"
                  minW="24px"
                  w="24px"
                >
                  <CloseIcon boxSize="20px" />
                </Box>
              ) :
              null}
          </Flex>
        ) :
        inputEl}

      {(autocomplete.loading || isLoadingDetails) && (
        <Text
          color="gray.500"
          fontSize="xs"
          {...(inputRowProps ?
            {
              left: 0,
              mt: 2,
              position: 'absolute',
              top: '100%',
              zIndex: 2,
            } :
            {mt: 1})}
        >
          {isLoadingDetails
            ? formatMessage({
                id: 'address_autocomplete.loading_details',
                defaultMessage: 'Loading address details...',
              })
            : formatMessage({
                id: 'address_autocomplete.loading_suggestions',
                defaultMessage: 'Loading suggestions...',
              })}
        </Text>
      )}

      {autocomplete.error && (
        <Text
          color="red.500"
          fontSize="xs"
          {...(inputRowProps ?
            {
              left: 0,
              mt: autocomplete.loading || isLoadingDetails ? 8 : 2,
              position: 'absolute',
              top: '100%',
              zIndex: 2,
            } :
            {mt: 1})}
        >
          {autocomplete.error}
        </Text>
      )}

      <AddressAutocompleteUI
        predictions={autocomplete.predictions}
        variant={uiVariant}
        onSelectPrediction={handleSelectPrediction}
      />
    </Box>
  );
};

AddressAutocomplete.displayName = 'AddressAutocomplete';

AddressAutocomplete.propTypes = {
  /**
   * Callback function called when a prediction is selected
   * @param {Object} prediction - The selected prediction object
   */
  onSelectPrediction: PropTypes.func,

  /**
   * Callback function called with parsed address components after place details are fetched
   * @param {Object} address - Parsed address with address1, city, stateCode, postalCode, countryCode
   */
  onAddressSelect: PropTypes.func,

  /**
   * Country code to filter results (e.g., 'AU', 'US', 'GB')
   */
  countryCode: PropTypes.string,

  /**
   * Placeholder text for the input field
   */
  placeholder: PropTypes.string,

  /**
   * Minimum characters required before triggering autocomplete
   */
  minChars: PropTypes.number,

  /**
   * Debounce delay in milliseconds
   */
  debounceMs: PropTypes.number,

  /**
   * Controlled value (use with onChange for react-hook-form)
   */
  value: PropTypes.string,

  /**
   * Called when value changes (typing or selection)
   */
  onChange: PropTypes.func,

  /**
   * Props passed through to the inner Input (e.g. for Field theme styling)
   */
  inputProps: PropTypes.object,

  /** Programmatic label — `id` on the input */
  inputId: PropTypes.string,

  /** Accessible name when no visible label is associated */
  ariaLabel: PropTypes.string,

  /** Points to visible label element id */
  ariaLabelledBy: PropTypes.string,

  inputRowProps: PropTypes.object,

  startElement: PropTypes.node,

  showClearButton: PropTypes.bool,

  uiVariant: PropTypes.string,

  clearButtonProps: PropTypes.object,
};

export default AddressAutocomplete;
